import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import RoleDropdown from './components/RoleDropdown';
import Home from './pages/Home';
import ResumeFilteringAgent from './pages/ResumeFilteringAgent';
import InventoryAgent from './pages/InventoryAgent';
import AdminPanel from './pages/AdminPanel';
import DeleteRole from './pages/DeleteRole';
import UploadResumes from './pages/UploadResumes';
import ViewResumes from './pages/ViewResumes';
import { API_CONFIG } from './config/api';



const App = () => {
  const [currentPage, setCurrentPage] = useState("home");
  const [selectedRole, setSelectedRole] = useState(null);
  const [roles, setRoles] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);

  /* ---------------------------------------
        LOAD ROLES
  --------------------------------------- */
  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    if (selectedRole) loadResumes(selectedRole);
  }, [selectedRole]);

  const loadRoles = async () => {
    try {
      const response = await fetch(`${API_CONFIG.RESUME_API}/list-folders`);
      const data = await response.json();
      setRoles(data.folders || []);
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  };

  /* ---------------------------------------
        LOAD RESUMES
  --------------------------------------- */
  const loadResumes = async (role, page = 1, pageSize = 10) => {
    try {
      const response = await fetch(`${API_CONFIG.RESUME_API}/get-resumes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          folder_name: role.slice(0, -1),
          page: page,
          page_size: pageSize
        }),
      });

      const paginationData = await response.json();

      const resumesResponse = await fetch(`${API_CONFIG.RESUME_API}/load_resumes_to_model`);
      const resumesData = await resumesResponse.json();

      return {
        resumes: resumesData || [],
        pagination: {
          total_resumes: paginationData.total_resumes,
          current_page: paginationData.current_page,
          page_size: paginationData.page_size,
          has_more: paginationData.has_more,
          resumes_in_batch: paginationData.resumes_in_batch
        }
      };
    } catch (error) {
      console.error("Error fetching resumes:", error);
      return { resumes: [], pagination: null };
    }
  };

  /* ---------------------------------------
        DELETE RESUME
  --------------------------------------- */
  const handleDeleteResume = async (role, fileName) => {
    try {
      const response = await fetch(`${API_CONFIG.RESUME_API}/delete-resume`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          folder_name: role.slice(0, -1),
          file_name: fileName
        }),
      });

      if (!response.ok) throw new Error("Failed to delete resume");

      const data = await response.json();
      alert(data.message);
      
      // Reload resumes after deletion
      if (selectedRole) {
        loadResumes(selectedRole);
      }

      return true;
    } catch (error) {
      console.error("Delete resume error:", error);
      alert("Error deleting resume");
      return false;
    }
  };

  /* ---------------------------------------
        DELETE ALL RESUMES
  --------------------------------------- */
  const handleDeleteAllResumes = async (role) => {
    const confirmDelete = window.confirm(`Delete ALL resumes from role "${role.slice(0, -1)}"? This action cannot be undone.`);
    if (!confirmDelete) return false;

    try {
      const response = await fetch(`${API_CONFIG.RESUME_API}/delete-all-resumes`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          folder_name: role.slice(0, -1)
        }),
      });

      if (!response.ok) throw new Error("Failed to delete all resumes");

      const data = await response.json();
      alert(data.message);
      
      return true;
    } catch (error) {
      console.error("Delete all resumes error:", error);
      alert("Error deleting all resumes");
      return false;
    }
  };

  /* ---------------------------------------
        DELETE ROLE
  --------------------------------------- */
  const handleRoleDelete = async (role) => {
    if (!role) return;

    const confirmDelete = window.confirm(`Delete role "${role.slice(0, -1)}"?`);
    if (!confirmDelete) return;

    try {
      const response = await fetch(`${API_CONFIG.RESUME_API}/delete-folder`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folder_name: role }),
      });

      if (!response.ok) throw new Error("Failed to delete role");

      const msg = await response.text();
      alert(msg);

      await loadRoles();
      setSelectedRole(null);
      setCurrentPage("admin");

    } catch (err) {
      console.error("Delete Error:", err);
      alert("Error deleting role");
    }
  };

  /* ---------------------------------------
        UPLOAD RESUMES
  --------------------------------------- */
  const handleUploadResumes = async (role, files) => {
    try {
      const formData = new FormData();
      formData.append("selected_role", role.replace("/", ""));

      Array.from(files).forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(`${API_CONFIG.RESUME_API}/upload`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      console.log("Upload response:", data);

      alert("Resumes uploaded successfully!");
    } catch (error) {
      console.error("Upload Error:", error);
      alert("Error uploading resumes");
    }
  };

  /* ---------------------------------------
        NAVIGATION
  --------------------------------------- */
  const handleNavigate = (page) => setCurrentPage(page);

  const adminPages = ["admin", "delete-role", "upload", "view-resumes"];
  const showAdminSidebar = adminPages.includes(currentPage);
  const showRoleDropdown = ["recruiter", "creyente", "inventory"].includes(currentPage);

  return (
    <div className="flex flex-col h-screen bg-white font-['Inter','Segoe UI','Roboto',sans-serif]">

      {/* NAVBAR */}
      {!showAdminSidebar && <Navbar onNavigate={handleNavigate} />}

      <div className="flex flex-1 overflow-hidden">

        {/* ADMIN SIDEBAR */}
        {showAdminSidebar && (
          <Sidebar
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}

        {/* PAGES */}
        {currentPage === "home" && <Home onNavigate={handleNavigate} />}

        {currentPage === "recruiter" && (
          <ResumeFilteringAgent
            selectedRole={selectedRole}
            roles={roles}
            onRoleSelect={setSelectedRole}
          />
        )}

        {currentPage === "inventory" && (
          <InventoryAgent />
        )}

        {currentPage === "creyente" && (
          <InventoryAgent />
        )}

        {currentPage === "admin" && (
          <AdminPanel roles={roles} onRoleCreate={loadRoles} />
        )}

        {currentPage === "delete-role" && (
          <DeleteRole roles={roles} onRoleDelete={handleRoleDelete} />
        )}

        {currentPage === "upload" && (
          <UploadResumes roles={roles} onUpload={handleUploadResumes} />
        )}

        {currentPage === "view-resumes" && (
          <ViewResumes 
            roles={roles} 
            fetchResumes={loadResumes}
            onDeleteResume={handleDeleteResume}
            onDeleteAllResumes={handleDeleteAllResumes}
          />
        )}
      </div>

      {/* ROLE DROPDOWN */}
      {showRoleDropdown && (
        <RoleDropdown
          roles={roles}
          selectedRole={selectedRole}
          selectedAgent={selectedAgent}
          onNavigate={handleNavigate}
          setSelectedAgent={setSelectedAgent}
        />
      )}
    </div>
  );
};

export default App;
